import type { TFunction } from 'i18next';
import { formatClock } from '@/features/trips/format';

/**
 * A forward-compatible fallback row for an alert `type` this build does not know
 * (slice 83, section F). The web feed is a discriminated union whose dispatcher
 * (`FeedRow`) is exhaustive at COMPILE time — but at RUNTIME an older deployed
 * build, or a client holding a cached service worker, can receive a NEWER alert
 * type. Rather than crash the whole list (the old `assertNever` threw) it renders
 * here: the humanised server `type`, the vehicle, the time, and an honest "details
 * not available in this version", still tappable and markable read.
 *
 * The minimal shape it reads — `id`, `vehicleId`, `read`, `observedAt`, `type` — is
 * carried by EVERY feed item on the wire (api `alerts/routes.ts`), so a future type
 * built on the same base still renders. It never invents a location, so it is a
 * plain button (mark-read), never a map-select.
 *
 * Tokens only — no colour literal.
 */
export interface GenericWireAlert {
  id: string;
  vehicleId: string;
  read: boolean;
  type?: unknown;
  observedAt?: unknown;
}

/** Turn a wire type token (`some_new_alert`) into readable text (`Some new alert`). */
function humaniseAlertType(type: unknown): string | null {
  if (typeof type !== 'string' || type.trim() === '') return null;
  const words = type.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function GenericAlertRow({
  item,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  item: GenericWireAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: () => void;
  t: TFunction;
}) {
  const title = humaniseAlertType(item.type) ?? t('alerts.generic.fallbackTitle');
  const observedMs = typeof item.observedAt === 'string' ? Date.parse(item.observedAt) : NaN;
  const observedTime = Number.isNaN(observedMs) ? null : formatClock(locale, observedMs, timeZone);
  const dotClass = item.read ? 'bg-transparent' : 'bg-raad-slate';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        data-testid="generic-alert-row"
        data-alert-id={item.id}
        className={[
          'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <span className="truncate text-sm font-medium text-raad-muted">{title}</span>
            {!item.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}
            {observedTime ? (
              <div className="mt-1 text-[12px] text-raad-muted">
                {t('alerts.observedAt', { time: observedTime })}
              </div>
            ) : null}
            <div className="mt-1 text-[11.5px] text-raad-dim">{t('alerts.generic.body')}</div>
          </div>
        </div>
      </button>
    </li>
  );
}
